import { dia } from "@clientio/rappid";
import * as ace from "ace-builds";
import { StateView, State } from "../mvc/state";
import { AceFactory } from "../language-server/ace-factory";
import { PreferenceStore } from "../services/preference-store";
import AceLanguageServer from "../language-server/ace-lsp-adapter";
import { setSyntaxHighlighting } from "../language-server/ace-settings";
import { actionState } from "./action-state";
import { TransitionView, Transition } from "../mvc/transition";

export class AceEditorTransition {
  private containerGroup: SVGGElement;
  private foreignObject: SVGForeignObjectElement;
  private editorDiv: HTMLDivElement;
  private editor: ace.Ace.Editor;
  private transitionView: TransitionView;

  mount(cellView: dia.CellView): void {
    this.transitionView = cellView as TransitionView;
    const transition = this.transitionView.getModel();

    const labelIndex = transition.getLabelIndex();
    const boundingRect = this.transitionView.getLabelBBox(labelIndex);
    const label = transition.label(labelIndex);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const position = this.transitionView.getLabelCoordinates(label?.position as any);

    const width = Math.max(15, boundingRect.width + 3);
    const height = boundingRect.height + 3;

    const x = position.x - boundingRect.width / 2;
    const y = position.y - boundingRect.height / 2;

    this.containerGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    this.containerGroup.setAttribute("class", "ace-editor");

    this.foreignObject = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
    this.foreignObject.setAttribute("x", x.toString());
    this.foreignObject.setAttribute("y", y.toString());
    this.foreignObject.setAttribute("width", width.toString());
    this.foreignObject.setAttribute("height", height.toString());
    this.foreignObject.style.pointerEvents = "none";

    this.editorDiv = document.createElementNS("http://www.w3.org/1999/xhtml", "div") as HTMLDivElement;
    this.editorDiv.setAttribute("id", "ace-highlighter-" + transition.id);
    this.editorDiv.className = "ace-highlighter-editor";
    this.editorDiv.style.borderWidth = "0px";
    const fontSize = transition.label(0).attrs?.text?.fontSize;
    if (fontSize) {
      this.editorDiv.style.fontSize = fontSize + "px";
    }

    this.foreignObject.appendChild(this.editorDiv);
    this.containerGroup.appendChild(this.foreignObject);
    this.transitionView.el.appendChild(this.containerGroup);

    this.editorDiv.setAttribute("model-id", "" + transition.id);

    const darkMode = PreferenceStore.INSTANCE.theme.isDark;
    this.editor = AceFactory.createEmbeddedEditor(this.editorDiv, {
      padding: 1,
      darkMode: darkMode,
    });

    const currentShowSpec = transition.getShowSpecification();
    const currentText = currentShowSpec ? transition.getSpecification() : transition.getDocumentation();
    this.editor.getSession().setValue(currentText || "");
    setSyntaxHighlighting(this.editor, !currentShowSpec);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.editor.on("change", (event: any) => {
      if (!this.editor.isFocused()) {
        return;
      }
      const labelIndex = transition.getLabelIndex();
      transition.label(labelIndex, {
        attrs: {
          text: { text: String(this.editor?.getSession().getValue()) },
        },
      });
    });

    this.editor.on("blur", () => {
      AceLanguageServer.getInstance().disconnect(this.editor);
      this.transitionView.trigger("batchcommand:store");
      this.setFocus(this.transitionView, false);
    });

    this.editor.on("focus", () => {
      if (transition.getShowSpecification()) {
        AceLanguageServer.getInstance().connect(this.editor, "transition");
      }
      this.transitionView.trigger("batchcommand:init");
    });

    transition.on("change:attrs", (model) => {
      const labelIndex = transition.getLabelIndex();
      const text = model.label(labelIndex)?.attrs?.text?.text;
      if (text !== this.editor.getValue()) {
        if (actionState.isUndo) {
          this.editor.undo();
        } else if (actionState.isRedo) {
          this.editor.redo();
        }
      }
    });

    this.editor.commands.removeCommand("Close");
    this.editor.commands.addCommand({
      name: "Close",
      exec: () => {
        this.editor.blur();
        this.setFocus(this.transitionView, false);
      },
      bindKey: { mac: "esc", win: "esc" },
    });

    if (this.editor) {
      this.editor.resize(true);
    }
  }

  unmount(): void {
    if (this.editor) {
      AceLanguageServer.getInstance().disconnect(this.editor);
      this.editor.destroy();
      this.editor = null;
    }
    if (this.containerGroup) {
      this.containerGroup.remove();
      this.containerGroup = null;
    }
    this.foreignObject = null;
    this.editorDiv = null;
    this.transitionView = null;
  }

  isMounted() {
    if (!this.transitionView) {
      return false;
    }
    const container = this.transitionView.el.querySelector(".ace-editor");
    return container !== null;
  }

  getEditor(): ace.Ace.Editor | undefined {
    return this.editor;
  }

  isFocused(): boolean {
    return this.editor?.isFocused() || false;
  }

  setFocus(_view: TransitionView, focus: boolean): void {
    if (!this.editor) return;

    this.editor.setReadOnly(!focus || PreferenceStore.INSTANCE.isReadOnly);
    if (focus) {
      this.foreignObject.style.pointerEvents = "all";
      this.editor.focus();
      this.editor.getSelection().moveCursorFileEnd();
      this.transitionView.halo?.removeHandle("editlabel");
    } else {
      if (this.foreignObject) {
        this.foreignObject.style.pointerEvents = "none";
      }
      this.editor.blur();
      this.editor.getSelection().clearSelection();
    }
  }

  resize(): void {
    if (!this.foreignObject || !this.editor || !this.transitionView) return;

    const transition = this.transitionView.getModel();

    const labelIndex = transition.getLabelIndex();
    const boundingRect = this.transitionView.getLabelBBox(labelIndex);
    const label = transition.label(labelIndex);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const position = this.transitionView.getLabelCoordinates(label?.position as any);

    const width = Math.max(15, boundingRect.width + 3);
    const height = boundingRect.height + 3;

    const x = position.x - boundingRect.width / 2;
    const y = position.y - boundingRect.height / 2;

    this.foreignObject.setAttribute("x", x.toString());
    this.foreignObject.setAttribute("y", y.toString());
    this.foreignObject.setAttribute("width", width.toString());
    this.foreignObject.setAttribute("height", height.toString());
    this.editorDiv.style.width = width + "px";
    this.editorDiv.style.height = height + "px";

    this.editor.resize();
    this.updateText();
  }

  updateText(): void {
    if (!this.editor || !this.transitionView) return;

    const transition = this.transitionView.getModel();
    const labelIndex = transition.getLabelIndex();
    const editorText = this.editor.getSession().getValue();
    const modelText = transition.label(labelIndex).attrs?.text?.text || "";

    if (editorText !== modelText) {
      this.editor.getSession().setValue(modelText);
    }
  }

  getEditorContainer(): HTMLDivElement | undefined {
    return this.editorDiv;
  }
}
